import type { ComputedRef, Ref } from 'vue'
import type {
  DateRequestErrors,
  PossibleInvalidKeys,
} from '~/components/date-request/dateRequestTypes'
import type { DateRequestFlow } from '~/composables/useDateRequestFlow'
import type { DateRequestFormModel } from '~/composables/useDateRequestFormState'
import { useFormValidation } from '~/composables/useFormValidation'
import { useDateRequest } from '~/composables/useDateRequest'

type RulesMap = Record<string, string>

type SubmissionMode = 'private' | 'applicable' | 'listing'

interface SubmissionEmit {
  (event: 'on-complete-request', dateRequestId?: string): void
  (event: 'on-complete-private-request' | 'onCloseDateRequest'): void
}

interface UseDateRequestSubmissionOptions {
  emit: SubmissionEmit
}

export interface DateRequestSubmission {
  errors: Ref<DateRequestErrors>
  invalidKeys: Ref<PossibleInvalidKeys[]>
  clearErrors: (field?: string) => void
  rules: ComputedRef<RulesMap>
  submit: () => Promise<void>
  loading: Ref<boolean>
  buttonText: ComputedRef<string>
  canUserBeContactedChecked: Ref<boolean>
  isContactConfirmationChecked: Ref<boolean>
  handleClickSeeMore: () => void
}

/**
 * Owns validation rules (as a computed derived from `flow`), a single
 * `submit()` entry that branches on the active flow, and the
 * confirmation-checkbox state for the private path.
 */
export const useDateRequestSubmission = (
  form: Ref<DateRequestFormModel>,
  flow: DateRequestFlow,
  options: UseDateRequestSubmissionOptions
): DateRequestSubmission => {
  const { emit } = options

  const { t } = useI18n()
  const router = useRouter()
  const localePath = useLocalePath()
  const eventData = useEventData()
  const { toggleDateRequestSidebar } = useUiState()
  const { requestEventDates: requestEventDatesComposable } = useDateRequest()

  const loading = ref(false)
  const canUserBeContactedChecked = ref(false)
  const isContactConfirmationChecked = ref(true)

  const mode = computed<SubmissionMode>(() => {
    if (flow.isPrivateEventRequest.value) {
      return 'private'
    }
    if (
      flow.isDateRequestApplicable.value ||
      flow.isInstantDateRequestSimplifiedUi.value
    ) {
      return 'applicable'
    }
    return 'listing'
  })

  const baseRules: RulesMap = {
    email: 'required|email',
    name: 'required|min:2',
    surname: 'required|min:2',
    phone: 'required|phoneNumber',
  }

  const dateRequestTicketsRule = computed(
    () =>
      `required|numeric|min:${eventGetters.getMinDateRequestTickets(
        eventData.value
      )}|max:${eventGetters.getMaxDateRequestTickets(eventData.value)}`
  )

  const instantTicketsRule = computed(
    () =>
      `required|numeric|min:${eventGetters.getMinInstantBookingTickets(
        eventData.value
      )}|max:${eventGetters.getMaxInstantBookingTickets(eventData.value)}`
  )

  /** Listing / edge paths: use instant bounds when DR is off but instant is on. */
  const listingTicketsRule = computed(() => {
    const ev = eventData.value
    if (
      eventGetters.isInstantBookingActive(ev) &&
      !eventGetters.isDateRequestActive(ev)
    ) {
      return instantTicketsRule.value
    }
    return dateRequestTicketsRule.value
  })

  const rules = computed<RulesMap>(() => {
    if (mode.value === 'private') {
      return {
        ...baseRules,
        numberOfTickets: 'required',
      }
    }
    if (mode.value === 'applicable') {
      if (flow.isInstantDateRequestSimplifiedUi.value) {
        return {
          numberOfTickets: instantTicketsRule.value,
          dates: 'required|array',
        }
      }
      return {
        ...baseRules,
        phone: 'phoneNumber',
        numberOfTickets: dateRequestTicketsRule.value,
        dates: 'required|array',
        dateType: 'required|in:PUBLIC,PRIVATE',
      }
    }
    return {
      ...baseRules,
      numberOfTickets: listingTicketsRule.value,
    }
  })

  const { errors, clearErrors, validate, invalidKeys } = useFormValidation(
    form,
    rules
  ) as {
    errors: Ref<DateRequestErrors>
    invalidKeys: Ref<PossibleInvalidKeys[]>
    clearErrors: (field?: string) => void
    validate: (key?: string) => Promise<void>
  }

  // Clear validation state and reset the confirmation checkbox whenever we
  // flip between private and standard flows.
  watch(
    () => flow.isPrivateEventRequest.value,
    () => {
      clearErrors()
      isContactConfirmationChecked.value = true
    }
  )

  watch(canUserBeContactedChecked, (checked) => {
    isContactConfirmationChecked.value = checked
  })

  const hasBlockingErrors = () =>
    errors.value !== null && Object.keys(errors.value ?? {}).length > 0

  const submitPrivate = async () => {
    loading.value = true
    isContactConfirmationChecked.value = canUserBeContactedChecked.value
    await validate()

    if (hasBlockingErrors()) {
      loading.value = false
      return
    }
    if (!canUserBeContactedChecked.value) {
      loading.value = false
      return
    }

    try {
      await requestEventDatesComposable({
        eventDescriptionId: eventGetters.getId(eventData.value),
        form,
        isPrivateEvent: true,
        onSuccess: () => {
          emit('on-complete-private-request')
        },
      })
    } finally {
      loading.value = false
    }
  }

  const submitApplicable = async () => {
    if (!flow.isDateRequestApplicable.value) {
      return
    }
    await validate()
    if (form.value.dates?.length === 0) {
      loading.value = false
      return
    }
    if (hasBlockingErrors()) {
      loading.value = false
      return
    }

    if (form.value.dateType !== 'PUBLIC' && form.value.dateType !== 'PRIVATE') {
      if (flow.isInstantDateRequestSimplifiedUi.value) {
        const ev = eventData.value
        form.value.dateType = eventGetters.isInstantBookingPrivate(ev)
          ? 'PRIVATE'
          : 'PUBLIC'
      } else {
        loading.value = false
        return
      }
    }

    try {
      await requestEventDatesComposable({
        eventDescriptionId: eventGetters.getId(eventData.value),
        form,
        isPrivateEvent: false,
        onSuccess: (dateRequestId?: string) => {
          emit('on-complete-request', dateRequestId)
        },
      })
    } finally {
      loading.value = false
    }
  }

  const handleClickSeeMore = () => {
    toggleDateRequestSidebar(undefined)
    emit('onCloseDateRequest')
    router.push({ path: localePath('search') })
  }

  const submit = async () => {
    if (mode.value === 'private') {
      await submitPrivate()
      return
    }
    if (mode.value === 'applicable') {
      await submitApplicable()
      return
    }
    handleClickSeeMore()
  }

  const buttonText = computed(() => {
    if (
      flow.isDateRequestApplicable.value ||
      flow.isPrivateEventRequest.value ||
      flow.isInstantDateRequestSimplifiedUi.value
    ) {
      return t('subscribeToEventUpdatesModal.firstStep.requestDatesButtonLabel')
    }
    return t('dateRequest.eventSuggestions.seeMoreButton')
  })

  return {
    errors,
    invalidKeys,
    clearErrors,
    rules,
    submit,
    loading,
    buttonText,
    canUserBeContactedChecked,
    isContactConfirmationChecked,
    handleClickSeeMore,
  }
}
